import React, { useState } from "react";
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useNotifications } from './notifications';

const DialogForgotPassword = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError, NotificationComponent } = useNotifications();

  // Handle password reset request
  const handleForgotPassword = async () => {
    if (!email || email.trim() === '') {
      showError('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch("/api/user/forgotPwd", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      const data = await response.json();
      console.log("Password reset response:", data);

      // Check if the response contains a specific success message
      if (response.ok) {
        if (data.success) {
          // Use the backend's success message directly
          showSuccess(data.message || "Password reset instructions sent successfully.");
          // Add a delay before closing the dialog to allow the user to see the success message
          setTimeout(() => {
            onClose();
          }, 1500); // 1.5 seconds delay
        } else {
          // Handle backend error messages
          showError(data.error || data.message || "An unexpected error occurred.");
        }
      } else {
        // Handle HTTP error responses
        showError(data.error || data.message || `Error: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Password reset error:", error);
      showError("Error connecting to server. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form state when closed
  const handleClose = () => {
    setEmail("");
    onClose();
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {}} // Empty function to prevent auto-closing
        static // Add static prop to prevent closing on outside click
      >
        <NotificationComponent />
        
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-4">
                <div className="relative p-0">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black hover:bg-gray-800 transition-colors duration-200 text-white hover:text-gray-200 z-10"
                    aria-label="Close"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Header with border-bottom */}
                <div className="border-b border-gray-200 py-4 mb-8">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Reset Password
                  </Dialog.Title>
                </div>

                {/* Main content */}
                <div className="mt-8 px-4">
                  {/* Email Address */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-4">
                      <label htmlFor="email"
                        className="block text-sm font-medium text-gray-900 w-1/3"
                      >
                        Email Address
                      </label>
                      <div className="flex-grow">
                        <input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Enter your email address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && email.trim()) {
                              handleForgotPassword();
                            }
                          }}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-black text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer section with border */}
                <div className="border-t border-gray-200 mt-10">
                  <div className="flex justify-between mt-5">                 
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={isLoading}
                      className={`rounded-md bg-gray-200 text-gray-700 py-2 px-4 ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'} focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isLoading || !email}
                      className={`rounded-md ${isLoading || !email ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"} text-white py-2 px-4 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 min-w-[120px]`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Processing...
                        </div>
                      ) : "Reset Password"}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DialogForgotPassword;